import React, { useRef, useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import './AccountDetailsDialog.css';

export interface BankruptcyAccount {
  loan_number: string;
  customer_name: string;
  chapter: string;
  filing_date: string;
  days_past_due: number;
  last_payment_date: string;
  last_payment_type: string;
  deferments: number;
  secured?: boolean;
}

interface PaginationInfo {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

interface SortingInfo {
  sort_by: string;
  sort_order: string;
}

interface BankruptcyAccountsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  accounts: BankruptcyAccount[];
  title: string;
  chapter: '7' | '13';
  securedFilter: 'all' | 'secured' | 'unsecured';
  onSecuredFilterChange: (value: 'all' | 'secured' | 'unsecured') => void;
  loading?: boolean;
  errorMessage?: string | null;
  pagination?: PaginationInfo;
  sorting?: SortingInfo;
  onPageChange?: (page: number) => void;
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  onSearchChange?: (searchLoanNumber: string, searchCustomerName: string) => void;
  searchLoanNumber?: string;
  searchCustomerName?: string;
  isSearchPending?: boolean;
  onSearchComplete?: () => void;
  onExportAllData?: () => Promise<BankruptcyAccount[]>;
}

const COL_COUNT = 8;

const BankruptcyAccountsDialog: React.FC<BankruptcyAccountsDialogProps> = ({
  isOpen,
  onClose,
  accounts,
  title,
  chapter,
  securedFilter,
  onSecuredFilterChange,
  loading = false,
  errorMessage = null,
  pagination,
  sorting,
  onPageChange,
  onSortChange,
  onSearchChange,
  searchLoanNumber = '',
  searchCustomerName = '',
  isSearchPending = false,
  onSearchComplete,
  onExportAllData,
}) => {
  const loanNumberInputRef = useRef<HTMLInputElement>(null);
  const customerNameInputRef = useRef<HTMLInputElement>(null);
  const lastFocusedInputRef = useRef<'loan' | 'customer' | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');

  useEffect(() => {
    if (!isSearchPending && lastFocusedInputRef.current && onSearchComplete) {
      setTimeout(() => {
        if (lastFocusedInputRef.current === 'loan' && loanNumberInputRef.current) {
          loanNumberInputRef.current.focus();
          const len = loanNumberInputRef.current.value.length;
          loanNumberInputRef.current.setSelectionRange(len, len);
        } else if (lastFocusedInputRef.current === 'customer' && customerNameInputRef.current) {
          customerNameInputRef.current.focus();
          const len = customerNameInputRef.current.value.length;
          customerNameInputRef.current.setSelectionRange(len, len);
        }
        onSearchComplete();
      }, 50);
    }
  }, [isSearchPending, onSearchComplete]);

  if (!isOpen) return null;

  const totalAccountsCount = pagination?.total_count ?? accounts.length;

  const handleSort = (column: string) => {
    if (!onSortChange) return;
    const currentSortBy = sorting?.sort_by || 'days_past_due';
    const currentSortOrder = sorting?.sort_order || 'DESC';
    const newSortOrder = currentSortBy === column && currentSortOrder === 'DESC' ? 'ASC' : 'DESC';
    onSortChange(column, newSortOrder);
  };

  const getSortIcon = (column: string) => {
    if (!sorting || sorting.sort_by !== column) return '↕️';
    return sorting.sort_order === 'ASC' ? '↑' : '↓';
  };

  const cell = (v: string | number) => (v === '' || v == null ? '—' : String(v));

  const exportToExcel = async () => {
    if (!onExportAllData) {
      alert('Export is not available');
      return;
    }
    if (isExporting) return;
    try {
      setIsExporting(true);
      setExportProgress('Fetching all rows…');
      const all = await onExportAllData();
      if (all.length === 0) {
        alert('No data to export');
        return;
      }
      const exportData = all.map((a) => ({
        'Loan #': a.loan_number,
        Customer: a.customer_name,
        Chapter: a.chapter,
        'Filing date': a.filing_date,
        'Days past due': a.days_past_due,
        'Last payment date': a.last_payment_date,
        'Last payment type': a.last_payment_type,
        Deferments: a.deferments,
        Secured: a.secured == null ? '' : a.secured ? 'Secured' : 'Unsecured',
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bankruptcy Accounts');
      const ts = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      XLSX.writeFile(wb, `Bankruptcy_Chapter_${chapter}_${ts}.xlsx`);
      setExportProgress(`Exported ${all.length} rows.`);
    } catch (e) {
      console.error(e);
      setExportProgress('Export failed.');
    } finally {
      setIsExporting(false);
      setTimeout(() => setExportProgress(''), 3000);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-container" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{title}</h2>
          <button type="button" className="close-button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="dialog-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner" />
              <p>Loading bankruptcy accounts…</p>
            </div>
          ) : (
            <>
              {errorMessage ? (
                <div className="error-banner" style={{ marginBottom: 12 }}>
                  <p style={{ margin: 0 }}>{errorMessage}</p>
                </div>
              ) : null}

              <div className="summary-stats" style={{ marginBottom: 12 }}>
                <div className="stat-item">
                  <span className="stat-label">Total accounts:</span>
                  <span className="stat-value">{totalAccountsCount.toLocaleString()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Filter:</span>
                  <span className="stat-value">
                    <div style={{ display: 'inline-flex', gap: 8 }}>
                      <button
                        type="button"
                        className="clear-search-button"
                        onClick={() => onSecuredFilterChange('all')}
                        disabled={securedFilter === 'all'}
                        title="Show all (secured + unsecured)"
                      >
                        All
                      </button>
                      <button
                        type="button"
                        className="clear-search-button"
                        onClick={() => onSecuredFilterChange('secured')}
                        disabled={securedFilter === 'secured'}
                        title="Show secured only"
                      >
                        Secured
                      </button>
                      <button
                        type="button"
                        className="clear-search-button"
                        onClick={() => onSecuredFilterChange('unsecured')}
                        disabled={securedFilter === 'unsecured'}
                        title="Show unsecured only"
                      >
                        Unsecured
                      </button>
                    </div>
                  </span>
                </div>
              </div>

              {onSearchChange && (
                <div className="search-controls">
                  <div className="search-input-group">
                    <label htmlFor="bk-search-loan">Search by loan #:</label>
                    <input
                      ref={loanNumberInputRef}
                      id="bk-search-loan"
                      type="text"
                      className="search-input"
                      placeholder="Loan number…"
                      value={searchLoanNumber}
                      onChange={(e) => onSearchChange(e.target.value, searchCustomerName)}
                      onFocus={() => {
                        lastFocusedInputRef.current = 'loan';
                      }}
                    />
                  </div>
                  <div className="search-input-group">
                    <label htmlFor="bk-search-customer">Search by customer:</label>
                    <input
                      ref={customerNameInputRef}
                      id="bk-search-customer"
                      type="text"
                      className="search-input"
                      placeholder="Customer name…"
                      value={searchCustomerName}
                      onChange={(e) => onSearchChange(searchLoanNumber, e.target.value)}
                      onFocus={() => {
                        lastFocusedInputRef.current = 'customer';
                      }}
                    />
                  </div>
                  <div className="search-actions">
                    {isSearchPending && (
                      <div className="search-pending-indicator">
                        <span className="search-pending-text">Searching…</span>
                      </div>
                    )}
                    <button
                      type="button"
                      className="clear-search-button"
                      onClick={() => onSearchChange('', '')}
                      disabled={!searchLoanNumber && !searchCustomerName}
                    >
                      Clear search
                    </button>
                  </div>
                </div>
              )}

              <div className="table-container">
                <table className="accounts-table">
                  <thead>
                    <tr>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('loan_number')}
                        title="Loan number"
                      >
                        Loan # {onSortChange && getSortIcon('loan_number')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('customer_name')}
                        title="Customer name"
                      >
                        Customer {onSortChange && getSortIcon('customer_name')}
                      </th>
                      <th title="Bankruptcy chapter">Chapter</th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('filing_date')}
                        title="Filing date"
                      >
                        Filed {onSortChange && getSortIcon('filing_date')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('days_past_due')}
                        title="Days past due"
                      >
                        DPD {onSortChange && getSortIcon('days_past_due')}
                      </th>
                      <th title="Last payment date">Last pmt</th>
                      <th title="Last payment type">Pmt type</th>
                      <th title="Deferments (demo for now)">Defers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!Array.isArray(accounts) || accounts.length === 0 ? (
                      <tr>
                        <td colSpan={COL_COUNT} className="no-data">
                          No accounts found
                        </td>
                      </tr>
                    ) : (
                      accounts.map((a, index) => (
                        <tr key={`${a.loan_number}-${index}`} className={index % 2 === 0 ? 'even' : 'odd'}>
                          <td className="loan-number">{cell(a.loan_number)}</td>
                          <td className="customer-name">{cell(a.customer_name)}</td>
                          <td>{cell(a.chapter)}</td>
                          <td>{cell(a.filing_date)}</td>
                          <td>{cell(a.days_past_due)}</td>
                          <td>{cell(a.last_payment_date)}</td>
                          <td>{cell(a.last_payment_type)}</td>
                          <td>{cell(a.deferments)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {pagination && onPageChange && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Page {pagination.page} of {pagination.total_pages} ({pagination.total_count.toLocaleString()} total)
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button
                      type="button"
                      className="pagination-button"
                      onClick={() => onPageChange(pagination.page - 1)}
                      disabled={!pagination.has_previous || loading}
                    >
                      ← Previous
                    </button>
                    <button
                      type="button"
                      className="pagination-button"
                      onClick={() => onPageChange(pagination.page + 1)}
                      disabled={!pagination.has_next || loading}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="dialog-footer">
          <div className="export-section">
            <button
              type="button"
              className="export-excel-button"
              onClick={exportToExcel}
              disabled={loading || isExporting || !onExportAllData}
            >
              {isExporting ? '⏳ Exporting…' : '📊 Export to Excel'}
            </button>
            {exportProgress ? <span className="export-progress-text">{exportProgress}</span> : null}
          </div>
          <button type="button" className="close-dialog-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BankruptcyAccountsDialog;

